import type { ContextEntities } from './RenderContext';
import { readPath } from './substitutePlaceholders';

// Same token grammar as the inline placeholders, whole-field only: the field
// must be exactly one `{path.to.field}` token (surrounding whitespace ignored).
const MEDIA_PLACEHOLDER_RE = /^\{([\w.[\]-]+)\}$/;

/** What an entity image is, as far as a widget needs it: a URL and the
 * media library's alt text (see `imageAlt`). */
export interface EntityMedia {
  url: string;
  alternativeText?: string;
}

/**
 * Resolves an entity-image placeholder field (e.g. the Hero's
 * `backgroundOverride` set to `{city.image}`) against the render context's
 * entities. Returns the referenced media object when the path resolves to
 * something image-shaped (an object carrying a string `url`), and `undefined`
 * otherwise — empty field, malformed token, unknown path, or an entity whose
 * field is unset — so callers fall back to their own component-level image.
 */
export function resolveMediaPlaceholder(
  field: string | undefined,
  entities: ContextEntities,
): EntityMedia | undefined {
  const path = field ? MEDIA_PLACEHOLDER_RE.exec(field.trim())?.[1] : undefined;
  if (!path) {
    return undefined;
  }
  const value = readPath(entities, path);
  if (
    value != null &&
    typeof value === 'object' &&
    typeof (value as { url?: unknown }).url === 'string'
  ) {
    return value as EntityMedia;
  }
  return undefined;
}
